import { canUserViewOrgMapsAggregate } from "@/lib/orgAccess";
import { isOrgConsoleContributor } from "@/lib/orgConsoleRoles";
import type { UserRow } from "@/types/database";

/** True when Fit2Lift staff access has been switched off (F2L-61). */
export function isStaffDeactivated(user: UserRow | null): boolean {
  return Boolean(user?.deactivated_at);
}

/** Video Studio: HQ admin = superadmin OR admin with `content` permission. */
export function isHqAdmin(user: UserRow | null): boolean {
  if (!user || isStaffDeactivated(user)) return false;
  if (user.user_type === "superadmin") return true;
  if (user.user_type === "admin") return Boolean(user.permissions?.content);
  return false;
}

/**
 * Returns a path to redirect to when the current role must not access `pathname`.
 * `null` means the route is allowed.
 */
export function getRoleRouteRedirect(user: UserRow | null, pathname: string): string | null {
  if (!user) return null;

  // Deactivated staff must not keep SPA routes; AuthContext also signs them out.
  if (isStaffDeactivated(user)) {
    return "/login";
  }

  if (user.user_type === "orgadmin" && pathname === "/body-map") {
    return "/org";
  }

  if (pathname.startsWith("/admin/platform")) {
    return "/admin";
  }

  // F2L-60 / F2L-63: Settings (Users + Lists) and legacy /admin/users — superadmin only.
  if (
    pathname === "/admin/settings" ||
    pathname.startsWith("/admin/settings/") ||
    pathname === "/admin/users" ||
    pathname.startsWith("/admin/users/")
  ) {
    if (user.user_type !== "superadmin") {
      return user.user_type === "admin" ? "/admin" : "/org";
    }
  }

  if (pathname.startsWith("/studio") && !isHqAdmin(user)) {
    // Hard lock Studio to HQ content admins only.
    // Employees land on assigned program; org admins/managers land on org dashboard.
    if (user.user_type === "orguser" && user.role_type === "employee") return "/my";
    return user.user_type === "superadmin" || user.user_type === "admin" ? "/admin" : "/org";
  }

  // Employee portal: only orguser sees /my. Pipeline users (superadmin/admin) and orgadmin
  // are redirected since they have no employee program of their own.
  if (pathname === "/my" || pathname.startsWith("/my/")) {
    if (user.user_type !== "orguser") {
      return user.user_type === "superadmin" || user.user_type === "admin" ? "/admin" : "/org";
    }
  }

  const isEmployeeContributor =
    user.user_type === "orguser" && user.role_type === "employee";

  if (pathname.startsWith("/maps-overview")) {
    if (isEmployeeContributor) return "/org";
    if (!canUserViewOrgMapsAggregate(user)) {
      return user.user_type === "superadmin" || user.user_type === "admin" ? "/admin" : "/org";
    }
  }

  if (isOrgConsoleContributor(user) && pathname.startsWith("/org/employees/import")) {
    return "/org";
  }
  if (pathname.startsWith("/org/users/new") && user.user_type !== "orgadmin") {
    return "/org";
  }
  if (isEmployeeContributor && pathname.startsWith("/org/employees")) {
    return "/org";
  }

  const isPipeline = user.user_type === "superadmin" || user.user_type === "admin";
  if (isPipeline) {
    // /org is the org-admin home; pipeline users have no org_id — send them to the command center.
    if (pathname === "/org" || pathname === "/org/") {
      return "/admin";
    }
    return null;
  }

  if (!pathname.startsWith("/admin")) {
    return null;
  }

  const orgId = user.org_id;
  const detail = pathname.match(/^\/admin\/organizations\/([^/]+)(?:\/edit)?$/);
  if (detail?.[1] && orgId && detail[1] === orgId) {
    return null;
  }

  return "/org";
}

export function isPipelineUser(user: UserRow | null): boolean {
  if (!user || isStaffDeactivated(user)) return false;
  return user.user_type === "superadmin" || user.user_type === "admin";
}
